import React, { useCallback, useMemo, useState } from 'react';

import {
  clampLinkPreviewAspectRatio,
  classifyLinkPreviewSource,
  pickLinkPreviewHeroSource
} from '../../feed/echo/linkPreviewGeometry';
import { cn } from '../../lib/utils';
import { getMessageSurfaceClassName } from './messageStyleClasses';

// Layout contract: the card reserves its final pixel size on first paint
// (aspectRatio for the wide variant, fixed-size box for tall/small) so an
// image's natural load or error doesn't cause a height shift. Broken images
// are hidden inside the reserved bg box; variant geometry stays unchanged.

const safeHostname = (url) => {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

const LinkPreviewCard = ({ preview, isOwnMessage = false }) => {
  const [imageFailed, setImageFailed] = useState(false);

  const destinationUrl = preview.url;
  const host = useMemo(
    () => preview.publisher || safeHostname(destinationUrl),
    [destinationUrl, preview.publisher],
  );
  const description = preview.description || preview.title || host || destinationUrl;
  const heroSource = useMemo(() => pickLinkPreviewHeroSource(preview), [preview]);
  const variant = useMemo(
    () => classifyLinkPreviewSource(heroSource),
    [heroSource],
  );
  const wideAspectStyle = useMemo(
    () => ({ aspectRatio: clampLinkPreviewAspectRatio(heroSource?.width, heroSource?.height) }),
    [heroSource?.width, heroSource?.height],
  );

  const handleImageError = useCallback(() => {
    setImageFailed(true);
  }, []);

  if (!destinationUrl) return null;

  const wrapperClass = cn(
    'relative w-full max-w-lg min-w-0 overflow-hidden rounded-glass backdrop-blur-sm',
    getMessageSurfaceClassName(isOwnMessage),
  );
  const hostClass = cn(
    'truncate text-xs font-normal',
    isOwnMessage ? 'text-white/60 dark:text-neutral-500' : 'text-gray-500 dark:text-neutral-400',
  );
  const descriptionClass = cn(
    'line-clamp-2 text-cursed leading-4 font-medium',
    isOwnMessage ? 'text-white dark:text-neutral-300' : 'text-black dark:text-white',
  );

  const hostNode = host ? <div className={hostClass}>{host}</div> : null;
  const descriptionNode = description ? <div className={descriptionClass}>{description}</div> : null;

  if (variant === 'tall') {
    return (
      <div className={wrapperClass} data-app-region="none">
        <a
          href={destinationUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-h-32 min-w-0 cursor-pointer items-stretch overflow-hidden no-underline"
        >
          <div className="w-32 shrink-0 overflow-hidden bg-black/10 dark:bg-black/20">
            <img
              src={heroSource.url}
              alt=""
              onError={handleImageError}
              className={cn('size-full object-cover', imageFailed && 'hidden')}
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 px-5 py-4">
            {hostNode}
            {descriptionNode}
          </div>
        </a>
      </div>
    );
  }

  if (variant === 'small') {
    return (
      <div className={wrapperClass} data-app-region="none">
        <a
          href={destinationUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-w-0 cursor-pointer items-center gap-4 px-5 py-4 no-underline"
        >
          <div className="size-12 shrink-0 overflow-hidden rounded bg-black/10 dark:bg-black/20">
            <img
              src={heroSource.url}
              alt=""
              onError={handleImageError}
              className={cn('size-full object-contain', imageFailed && 'hidden')}
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {hostNode}
            {descriptionNode}
          </div>
        </a>
      </div>
    );
  }

  return (
    <div className={wrapperClass} data-app-region="none">
      <a
        href={destinationUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block min-w-0 cursor-pointer overflow-hidden no-underline"
      >
        {variant === 'wide' && heroSource && (
          <div className="w-full overflow-hidden bg-black/10 dark:bg-black/20" style={wideAspectStyle}>
            <img
              src={heroSource.url}
              alt=""
              onError={handleImageError}
              className={cn('size-full object-cover', imageFailed && 'hidden')}
            />
          </div>
        )}

        <div className="flex min-w-0 flex-col gap-2 px-5 pt-4 pb-6">
          {hostNode}
          {descriptionNode}
        </div>
      </a>
    </div>
  );
};

export default LinkPreviewCard;
